<template>
  <card title="Clinic's logo">
    <div class="clinic-logo">
      <div class="clinic-logo__preview">
        <img v-if="clinic.logo" :src="clinic.logo" class="clinic-logo__image" alt="Clinic logo">
        <div v-else class="clinic-logo__empty">No logo</div>
      </div>

      <div class="clinic-logo__controls">
        <p class="clinic-logo__hint">
          Used in the TOP Clinics block. PNG with a transparent background works best —
          the file is fitted into {{ maxWidth }}×{{ maxHeight }} without cropping, so nothing
          of the mark is cut off. Smaller logos are kept at their own size.
        </p>
        <div class="clinic-logo__actions">
          <base-button type="primary" size="sm" :disabled="uploading" @click="$refs.logo.click()">
            {{ clinic.logo ? 'Replace logo' : 'Upload logo' }}
          </base-button>
          <base-button
            v-if="clinic.logo"
            type="danger"
            size="sm"
            :disabled="uploading"
            @click="removeLogo"
          >
            Remove
          </base-button>
        </div>
        <input
          ref="logo"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          class="clinic-logo__input"
          @change="handleFileUpload"
        >
      </div>
    </div>
  </card>
</template>

<script>
import APIClinics from '@/api/modules/clinics';
import { mapState } from 'vuex';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export default {
  name: 'ClinicLogo',
  data() {
    return {
      uploading: false,
      maxWidth: 400,
      maxHeight: 200,
    };
  },
  computed: {
    ...mapState('clinics', ['clinic']),
  },
  methods: {
    handleFileUpload() {
      const file = this.$refs.logo.files[0];
      if (!file) {
        return undefined;
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        alert('The logo has to be a PNG, JPEG or WebP image.');
        this.$refs.logo.value = '';
        return undefined;
      }

      this.uploading = true;
      this.$store.dispatch('setLoader', true);

      return APIClinics.uploadLogo({ file, clinicId: this.clinic.id })
        .then(() => this.$emit('get-clinic-event'))
        .finally(() => {
          // the same file has to be selectable again after a failed attempt
          this.$refs.logo.value = '';
          this.uploading = false;
          this.$store.dispatch('setLoader', false);
        });
    },
    removeLogo() {
      this.uploading = true;
      this.$store.dispatch('setLoader', true);

      return APIClinics.deleteLogo({ clinicId: this.clinic.id })
        .then(() => this.$emit('get-clinic-event'))
        .finally(() => {
          this.uploading = false;
          this.$store.dispatch('setLoader', false);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.clinic-logo {
  display: flex;
  align-items: flex-start;
  gap: 20px;

  &__preview {
    width: 200px;
    min-height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #dfe1e5;
    border-radius: 3px;
    /* a checkerboard shows whether the transparency survived the upload */
    background-image:
      linear-gradient(45deg, #eee 25%, transparent 25%),
      linear-gradient(-45deg, #eee 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #eee 75%),
      linear-gradient(-45deg, transparent 75%, #eee 75%);
    background-size: 16px 16px;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0;
  }

  &__image {
    max-width: 100%;
    max-height: 100px;
    object-fit: contain;
  }

  &__empty {
    color: rgba(0, 0, 0, 0.38);
    font-size: 13px;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    /* base-button carries its own margin, which is what glued the two
       together; the gap above is the only spacing this row needs. */
    .btn {
      margin: 0;
    }
  }

  &__hint {
    font-size: 13px;
    line-height: 140%;
    color: rgba(0, 0, 0, 0.38);
    margin-bottom: 10px;
  }

  &__input {
    position: absolute;
    left: -5000px;
  }
}
</style>
